import { Fragment, lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid2 as Grid,
  Skeleton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import Images from "../../../assets/js/Images";
import SectionSkeleton from "../../../components/ui/SectionSkeleton";
import {
  FILE_PATH_URI,
  checkUserType,
} from "../../../utils/helper";
import { handleRetrieveSpecialistDetails } from "../../../redux/public-slices/specialistappointment.slice";
import { APPOINTMENT_SLOTS, FEATURE_GUARD, GUEST_LOGIN } from "../../../utils/keywords";
import { showDialog, showDialogWithData } from "../../../redux/guest-slices/other.slice";
import Icons from "../../../assets/js/Icons";
import NavLink from "../../../layout/common/Footer/NavLink";
import { useChatInitiate } from "../../../hooks/useChatInitiate";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import Availability from "../../../components/ui/availability";
import moment from "moment";

const TopBannerA = lazy(() => import("../../../components/common/TopBannerA"));

function SingleSpecilalist() {
  const dispatch = useDispatch();
  const checkUser = checkUserType();
  const { id: specialistId } = useParams();
  const [specialist, setSpecialist] = useState(null);

  const { loadings } = useSelector((state) => state.otherSlice);
  const { initiateGeneralChat } = useChatInitiate();
  const { format: formatLocalPrice } = useExchangeRate();

  const { subscription, plan, features } = useSelector(
    (state) => state.subscriptionSlice
  );

  const isSubscribed = Boolean(
    subscription?.hasSubscription ||
    (plan?.name && plan?.name !== "basic") ||
    features?.includes("CHAT_ACCESS_LIMITED") ||
    features?.includes("CHAT_ACCESS_UNLIMITED")
  );

  const hasAccess = features?.includes(
    "APPOINTMENT_UNLIMITED"
  );



  const handleRetrieve = async () => {
    const response = await dispatch(
      handleRetrieveSpecialistDetails({ specialistId })
    ).unwrap();

    const specialistData = response?.specialist;
    setSpecialist(specialistData);

  };

  const handleLogin = () => {
    dispatch(showDialog(GUEST_LOGIN));
  };


  const openFollowUpModal = (item) => {
    if (!hasAccess) {
      dispatch(
        showDialog(FEATURE_GUARD, {
          title: "Appointment Booking Locked",
          description:
            "Upgrade to Premium or Platinum to book appointments with specialists.",
          requiredPlan: "Premium",
        })
      );

      return;
    }

    if (!checkUser) {
      handleLogin();
    } else {
      dispatch(
        showDialogWithData({
          type: APPOINTMENT_SLOTS,
          data: {
            specialistId: item?._id,
          },
        })
      )
    }
  }
  const handleChatInitiate = async (receiver) => {
    if (!checkUser) {
      handleLogin();
      return;
    }
    initiateGeneralChat(receiver);

  };


  useEffect(() => {
    handleRetrieve();
    // eslint-disable-next-line
  }, [specialistId]);

  if (loadings?.retrieveSingle) {
    return (
      <Fragment>
        <Suspense fallback={<SectionSkeleton />}>
          <TopBannerA heading="Loading..." currentPage="Specialist" />
        </Suspense>
        <Box sx={{ py: 10 }}>
          <Container maxWidth={false}>
            <Skeleton variant="rectangular" height={400} />
          </Container>
        </Box>
      </Fragment>
    );
  }

  if (!specialist) {
    return (
      <Fragment>
        <Suspense fallback={<SectionSkeleton />}>
          <TopBannerA heading="Not Found" currentPage="Specialist" />
        </Suspense>
        <Box sx={{ py: 10 }}>
          <Container maxWidth={false}>
            <Alert severity="error">Specialist not found!</Alert>
          </Container>
        </Box>
      </Fragment>
    );
  }


  const todayKey = String(moment().format("dddd")).toLowerCase();

  const startTime =
    specialist?.availability?.schedule?.[todayKey]?.startTime;
  const endTime =
    specialist?.availability?.schedule?.[todayKey]?.endTime;

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isChatAvailable =
    startTime &&
    endTime &&
    moment
      .tz(startTime, "HH:mm", specialist?.availability?.timeZone)
      .tz(userTimeZone)
      .format("HH:mm") <= moment().format("HH:mm") &&
    moment
      .tz(endTime, "HH:mm", specialist?.availability?.timeZone)
      .tz(userTimeZone)
      .format("HH:mm") >= moment().format("HH:mm");

  return (
    <Fragment>
      <Suspense fallback={<SectionSkeleton />}>
        <TopBannerA
          heading={specialist?.name}
          currentPage="Details "
          bannerImg={Images?.specialists}
          menus={[{ name: "Specialists", path: "/all-specialists" }]}
        />
      </Suspense>

      <Grid sx={{ py: 7, }} container justifyContent="center">
        <Grid size={{ xs: 12, laptop: 7 }}>

          <Stack
            sx={{
              bgcolor: "#f9f9f9a6"
            }}
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            p={4}
            alignItems={{ xs: "center", md: "flex-start" }}
          >
            <Avatar
              src={
                specialist?.profilePhoto
                  ? FILE_PATH_URI + specialist?.profilePhoto
                  : null
              }
              sx={{
                width: 120,
                height: 120,
              }}
            />

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: { xs: "center", md: "flex-start" },

              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                textTransform="capitalize"
                color="other.color1"
                sx={{ textAlign: { xs: "center", md: "left" } }}
              >
                {specialist?.name}
              </Typography>

              {/* Specializations */}
              <Stack
                direction="row"
                justifyContent={{ sm: "center", lg: "flex-start" }}
                flexWrap="wrap"
                gap={1}
              >
                {specialist?.specializationNames?.map((name, idx) => (
                  <Chip
                    key={idx}

                    label={name}
                    size="small"
                    sx={{


                      bgcolor: "#2686c824",
                      color: "#2686c8",
                      fontWeight: 500,
                    }}
                  />
                ))}
                {specialist?.pricing?.startingPrice != null && (
                  <Chip
                    label={`Starts at ${formatLocalPrice(specialist.pricing.startingPrice, specialist?.pricing?.currency)}`}
                    size="small"
                    sx={{
                      bgcolor: "#eef6ea",
                      color: "#2e7d32",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
              <Box sx={{ display: "flex", gap: 1.5 }}>

                <Tooltip title={!isSubscribed ? "Subscription plan required to chat with specialists" : !isChatAvailable ? "Specialist is currently offline" : ""}>
                  <span>
                    <Button
                      startIcon={<Icons.ChatStrokeRounded />}
                      LinkComponent={NavLink}
                      variant="text"
                      onClick={() => handleChatInitiate(specialist?._id)}
                      disabled={!isSubscribed || !isChatAvailable}
                      size="small"
                      sx={{
                        backgroundColor: (!isSubscribed || !isChatAvailable) && "transparent  !important",
                        color: (!isSubscribed || !isChatAvailable) && "#aaaaaa  !important",
                      }}
                    >
                      Chat
                    </Button>
                  </span>
                </Tooltip>

              </Box>
            </Box>


          </Stack>

          <Divider sx={{ my: 3 }} />


          <Box sx={{ px: { xs: "20px", md: "20px", lg: "0" } }}>
            {/* About SectionBox */}
            <Box  >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                mb={2}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="other.color1"
                >
                  About
                </Typography>
              </Stack>
              <Typography textAlign={"justify"} color="text.secondary">
                {specialist?.professionalBio}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              <Typography
                variant="h6"
                fontWeight="600"
                color="other.color1"
              >
                Professional experience
              </Typography>
              <Box>
                <Chip
                  label={`${specialist?.experience} Years Experience in Practice`}
                  size="small"
                  sx={{
                    bgcolor: "#fff9e2",
                    color: "#edc322",
                    fontWeight: 500,
                  }}
                /> </Box>

              <Box>
                <Stack
                  direction="row"
                  justifyContent={{ lg: "flex-start" }}
                  flexWrap="wrap"
                  gap={1}
                >
                  {specialist?.areaNames?.map((name, idx) => (
                    <Chip
                      key={idx}
                      label={name}
                      size="small"
                      sx={{
                        alignItems: "flex-start",
                        height: "auto",
                        py: 1,

                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          display: "block",
                        },
                        bgcolor: "#2686c824",
                        color: "#2686c8",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              <Typography
                variant="h6"
                fontWeight="600"
                color="other.color1"
              >
                Services offered
              </Typography>


              <Box sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                flexDirection: { xs: "column", sm: "row" },
              }}>
                <Button
                  startIcon={<Icons.Appointment />}
                  LinkComponent={NavLink}
                  disabled={!(specialist?.schedule)}
                  onClick={() => openFollowUpModal(specialist)}
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: "30px", px: 2.5, py: 1.5, color: "white" }}
                >
                  Book Appointment
                </Button>

                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  LinkComponent={NavLink}
                  disabled={!isChatAvailable}
                  startIcon={<Icons.ChatStrokeRounded />}
                  onClick={() => handleChatInitiate(specialist?._id)}
                  sx={{ borderRadius: "30px", px: 2.5, py: 1.5, color: "white" }}
                >
                  Chat
                </Button>

                <Availability
                  trigger={
                    <Button
                      color="dark"
                      size="small"
                      variant="contained"
                      startIcon={<Icons.Alert />}
                      sx={{ borderRadius: "30px", px: 2.5, py: 1.5, color: "white" }}
                    >
                      Chat avaliablity
                    </Button>
                  }
                  availability={specialist?.availability}
                />
              </Box>
            </Box>


          </Box>
        </Grid>


      </Grid>

    </Fragment >
  );
}

export default SingleSpecilalist;
